'use client';

/**
 * useSearchAiz Hook
 *
 * Core search hook that manages search state and operations.
 * Provides:
 * - Search execution with debouncing
 * - Result state management
 * - Keyboard navigation
 * - Filter management
 */

import logger from '@/lib/logger';
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { searchClient, SearchApiError } from '../api/searchClient';
import { navigateToSafeUrl } from '../utils/safeNavigation';
import type {
  SearchState,
  SearchActions,
  SearchResult,
  SearchFilters,
  SearchPattern,
  RerankConfig,
  AnalysisConfig,
  ConversationTurn,
  AiAnalysis,
  FacetCount,
} from '../types';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Default debounce delay for search input (ms)
 */
const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Minimum query length to trigger search
 */
const MIN_QUERY_LENGTH = 2;

// =============================================================================
// HOOK
// =============================================================================

export interface UseSearchAizOptions {
  /** Data sources to search */
  sources: string[];
  /** Search pattern to use */
  pattern?: SearchPattern;
  /** Number of results to fetch */
  limit?: number;
  /** Rerank configuration */
  rerank?: RerankConfig;
  /** Analysis configuration */
  analysis?: AnalysisConfig;
  /** Debounce delay in ms */
  debounceMs?: number;
  /** Callback when search completes */
  onSearchComplete?: (results: SearchResult[], query: string) => void;
  /** Callback when result is selected */
  onResultSelect?: (result: SearchResult) => void;
  /** Enable conversational mode */
  conversational?: boolean;
}

/** Shown when a whole search fails: a sentence, never a status code. */
export const SEARCH_UNAVAILABLE_MESSAGE = "Search isn't available right now. Please try again in a moment.";

/** One plain sentence per source that could not be searched. */
export function unavailableSourceNotice(label: string): string {
  return `${label} couldn't be searched right now.`;
}

export interface UseSearchAizReturn extends SearchState, SearchActions {
  /** Plain sentences for sources that could not be searched (never silent). */
  notices: string[];
  /** Whether the search input should be focused */
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Main search hook
 */
export function useSearchAiz(options: UseSearchAizOptions): UseSearchAizReturn {
  const {
    sources,
    pattern = 'unified',
    limit = 10,
    rerank,
    analysis,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    onSearchComplete,
    onResultSelect,
    conversational = false,
  } = options;

  // State
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<AiAnalysis | undefined>();
  const [conversationHistory, setConversationHistory] = useState<ConversationTurn[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [facets, setFacets] = useState<Record<string, FacetCount[]>>({});

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const pendingResolveRef = useRef<(() => void) | null>(null);
  const conversationId = useRef<string | undefined>(undefined);
  const abortController = useRef<AbortController | null>(null);

  /**
   * Execute search
   */
  const executeSearch = useCallback(
    async (searchQuery: string) => {
      // Cancel any pending search
      if (abortController.current) {
        abortController.current.abort();
      }

      // Skip if query too short
      // A new query starts with no stale "couldn't be searched" notices.
      setNotices([]);

      if (searchQuery.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setTotalCount(0);
        setAiAnalysis(undefined);
        setFacets({});
        setError(null);
        return;
      }

      const controller = new AbortController();
      abortController.current = controller;
      setIsSearching(true);
      setError(null);

      try {
        // Build context from conversation history for conversational mode
        let previousContext: string | undefined;
        if (conversational && conversationHistory.length > 0) {
          previousContext = conversationHistory
            .map((turn) => `Q: ${turn.query}\nKey findings: ${turn.analysis?.summary || 'N/A'}`)
            .join('\n\n');
        }

        // Execute search
        const response = conversational
          ? await searchClient.conversationalSearch(
              searchQuery,
              sources,
              conversationId.current,
              previousContext,
              controller.signal
            )
          : await searchClient.search({
              query: searchQuery,
              sources,
              pattern,
              limit,
              rerank,
              analysis,
              filters,
              signal: controller.signal,
            });

        if (controller.signal.aborted || abortController.current !== controller) {
          return;
        }

        // Update state
        setResults(response.results);
        setNotices((response.unavailableSources ?? []).map(unavailableSourceNotice));
        setTotalCount(response.totalCount);
        setAiAnalysis(response.analysis);
        setFacets(response.facets || {});
        setSelectedIndex(response.results.length > 0 ? 0 : -1);

        // Store conversation ID for follow-ups
        if (response.conversationId) {
          conversationId.current = response.conversationId;
        }

        // Add to conversation history
        if (conversational) {
          setConversationHistory((prev) => [
            ...prev,
            {
              id: `turn_${Date.now()}`,
              query: searchQuery,
              results: response.results,
              analysis: response.analysis,
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        // Callback
        onSearchComplete?.(response.results, searchQuery);
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        // The officer reads a sentence; the status/exception goes to the log.
        logger.warn('useSearchAiz', 'Search failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        setError(new Error(SEARCH_UNAVAILABLE_MESSAGE));
        setNotices([]);
        setResults([]);
        setTotalCount(0);
      } finally {
        if (abortController.current === controller) {
          setIsSearching(false);
        }
      }
    },
    [
      sources,
      pattern,
      limit,
      rerank,
      analysis,
      filters,
      conversational,
      conversationHistory,
      onSearchComplete,
    ]
  );

  /**
   * Debounced search
   */
  const search = useCallback(
    async (searchQuery: string): Promise<void> => {
      setQuery(searchQuery);

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      if (pendingResolveRef.current) {
        pendingResolveRef.current();
        pendingResolveRef.current = null;
      }

      // Return a promise that resolves after the debounced search completes
      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;
        debounceTimer.current = setTimeout(async () => {
          await executeSearch(searchQuery);
          pendingResolveRef.current = null;
          resolve();
        }, debounceMs);
      });
    },
    [executeSearch, debounceMs]
  );

  /**
   * Clear results
   */
  const clear = useCallback(() => {
    // Cancel pending search
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (pendingResolveRef.current) {
      pendingResolveRef.current();
      pendingResolveRef.current = null;
    }
    if (abortController.current) {
      abortController.current.abort();
    }

    setQuery('');
    setResults([]);
    setTotalCount(0);
    setError(null);
    setNotices([]);
    setAiAnalysis(undefined);
    setFacets({});
    setSelectedIndex(-1);
    setIsSearching(false);
  }, []);

  /**
   * Update filters and re-search
   */
  const updateFilters = useCallback(
    (newFilters: SearchFilters) => {
      setFilters(newFilters);
      // Re-execute search with new filters if we have a query
      if (query.length >= MIN_QUERY_LENGTH) {
        executeSearch(query);
      }
    },
    [query, executeSearch]
  );

  /**
   * Select result by index
   */
  const selectResult = useCallback(
    (index: number) => {
      if (index >= 0 && index < results.length) {
        setSelectedIndex(index);
      }
    },
    [results.length]
  );

  /**
   * Navigate selection with keyboard
   */
  const navigateSelection = useCallback(
    (direction: 'up' | 'down') => {
      setSelectedIndex((current) => {
        if (direction === 'up') {
          return current <= 0 ? results.length - 1 : current - 1;
        } else {
          return current >= results.length - 1 ? 0 : current + 1;
        }
      });
    },
    [results.length]
  );

  /**
   * Activate selected result
   */
  const activateSelected = useCallback(() => {
    if (selectedIndex >= 0 && selectedIndex < results.length) {
      const result = results[selectedIndex];
      onResultSelect?.(result);

      // Navigate if URL present
      navigateToSafeUrl(result.url);
    }
  }, [selectedIndex, results, onResultSelect]);

  /**
   * Add follow-up query (conversational mode)
   */
  const addFollowUp = useCallback(
    async (followUpQuery: string) => {
      if (!conversational) {
        console.warn('addFollowUp called but conversational mode is not enabled');
        return;
      }

      // Execute as a new search in conversation context
      await executeSearch(followUpQuery);
    },
    [conversational, executeSearch]
  );

  /**
   * Reset conversation
   */
  const resetConversation = useCallback(() => {
    setConversationHistory([]);
    conversationId.current = undefined;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return {
    // State
    query,
    isSearching,
    results,
    totalCount,
    error,
    notices,
    analysis: aiAnalysis,
    conversationHistory,
    selectedIndex,
    filters,
    facets,

    // Actions
    search,
    clear,
    setFilters: updateFilters,
    selectResult,
    navigateSelection,
    activateSelected,
    addFollowUp,
    resetConversation,

    // Refs
    inputRef,
  };
}

export default useSearchAiz;
